import type {Timeframe} from 'client';

import type {TimeframeFilter} from './types';

/** A timeframe name, such as H1 FY27. */
export type WorkstreamTimeframe = string;

/** Where the last `GET /api/v1/timeframes` response is kept between visits. */
export const TIMEFRAMES_CACHE_KEY = 'timeframes:v1';

const sortByStart = (timeframes: readonly Timeframe[]): Timeframe[] =>
  [...timeframes].sort((left, right) => Date.parse(left.starts_at) - Date.parse(right.starts_at));

const isTimeframe = (value: unknown): value is Timeframe =>
  typeof value === 'object' &&
  value !== null &&
  typeof (value as Timeframe).id === 'number' &&
  typeof (value as Timeframe).name === 'string' &&
  typeof (value as Timeframe).starts_at === 'string' &&
  typeof (value as Timeframe).ends_at === 'string';

function readCachedTimeframes(): Timeframe[] {
  try {
    const parsed: unknown = JSON.parse(window.localStorage.getItem(TIMEFRAMES_CACHE_KEY) ?? '[]');
    return Array.isArray(parsed) ? sortByStart(parsed.filter(isTimeframe)) : [];
  } catch {
    return [];
  }
}

// The API's list, starting from the last one this browser loaded. Empty on a
// first visit until the request returns.
let knownTimeframes: readonly Timeframe[] = readCachedTimeframes();

/** Timeframes from the last `GET /api/v1/timeframes`. Empty if none has loaded yet. */
export function getKnownTimeframes(): readonly Timeframe[] {
  return knownTimeframes;
}

// The list last passed to setKnownTimeframes, so passing it again is a no-op.
let knownTimeframesSource: readonly Timeframe[] | undefined;

/**
 * Called by `useTimeframes` on each render with a response, so non-React
 * helpers, and the next visit, see the API's list.
 */
export function setKnownTimeframes(timeframes: readonly Timeframe[]): void {
  if (timeframes === knownTimeframesSource) {
    return;
  }
  knownTimeframesSource = timeframes;
  // The generated client doesn't check the response; ignore one that isn't a
  // list (a proxy error page, say) rather than throw while rendering.
  if (!Array.isArray(timeframes)) {
    return;
  }
  knownTimeframes = sortByStart(timeframes.filter(isTimeframe));
  try {
    window.localStorage.setItem(TIMEFRAMES_CACHE_KEY, JSON.stringify(knownTimeframes));
  } catch {
    // Storage can be full or blocked; the in-memory list still works.
  }
}

export function getNavigationTimeframes(
  timeframes: readonly Timeframe[] = knownTimeframes
): Timeframe[] {
  return timeframes.filter((timeframe) => timeframe.show_in_navigation);
}

export function getTimeframeByName(
  name: string | null | undefined,
  timeframes: readonly Timeframe[] = knownTimeframes
): Timeframe | undefined {
  const trimmed = name?.trim();
  return trimmed ? timeframes.find((timeframe) => timeframe.name === trimmed) : undefined;
}

/**
 * Default planning timeframe when the URL has no `timeframe`: the latest
 * in-navigation timeframe that has started (the current one, when it's in
 * navigation), otherwise the first in-navigation timeframe. Empty when no
 * timeframes have loaded.
 */
export function getDefaultWorkstreamTimeframe(
  referenceDate: Date = new Date(),
  timeframes: readonly Timeframe[] = knownTimeframes
): WorkstreamTimeframe {
  const now = referenceDate.getTime();
  const navigation = getNavigationTimeframes(timeframes);
  const started = navigation.filter((timeframe) => Date.parse(timeframe.starts_at) <= now);
  const latestStarted = started[started.length - 1];
  return (latestStarted ?? navigation[0] ?? timeframes[0])?.name ?? '';
}

export function formatWorkstreamTimeframeLabel(timeframe: WorkstreamTimeframe): string {
  return timeframe.replace('FY', '20');
}

export function parseWorkstreamTimeframe(
  raw: string | null | undefined,
  timeframes: readonly Timeframe[] = knownTimeframes
): WorkstreamTimeframe {
  return (
    getTimeframeByName(raw, timeframes)?.name ??
    getDefaultWorkstreamTimeframe(new Date(), timeframes)
  );
}

/** Returns a timeframe only when the URL explicitly sets a known `timeframe` query value. */
export function parseWorkstreamTimeframeFromSearchParam(
  raw: string | null | undefined,
  timeframes: readonly Timeframe[] = knownTimeframes
): WorkstreamTimeframe | undefined {
  return getTimeframeByName(raw, timeframes)?.name;
}

/**
 * Maps a planning timeframe to its workstream table `timeframes` filter value,
 * falling back to the default timeframe's for an unknown name.
 */
export function narrativePlanningHalfToTimeframeTableFilter(
  narrative: WorkstreamTimeframe,
  timeframes: readonly Timeframe[] = knownTimeframes
): TimeframeFilter {
  const filterKey =
    getTimeframeByName(narrative, timeframes)?.filter_key ??
    getTimeframeByName(getDefaultWorkstreamTimeframe(new Date(), timeframes), timeframes)
      ?.filter_key ??
    '';
  return filterKey as TimeframeFilter;
}
